import React, { useState, useEffect, useCallback } from 'react';
import { Col, Row, Button, Spinner, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import $ from 'jquery';
import moment from 'moment'
import FalconComponentCard from 'components/common/FalconComponentCard';
import IconButton from 'components/common/IconButton';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import TextFilter from 'components/filters/textFilter';
import DateRangeFilter from 'components/filters/daterangeFilter';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axiosInstance from 'helpers/axiosInstance';
import debounce from 'lodash/debounce';
import AsyncSelect from 'react-select/async';
import { components } from "react-select";


const { ValueContainer, Placeholder } = components;

const columns = [
    {
        accessor: 'activeClient',
        Header: 'Active',
        sort: true,
        Cell: ({ value }) => (
            <div className="text-center">
                <FontAwesomeIcon
                    icon={value ? 'check' : 'times'}
                    className={value ? 'text-success' : 'text-danger'}
                />
            </div>
        )
    },
    {
        accessor: 'name',
        Header: 'Name',
        sort: true
    },
    {
        accessor: 'nationality.residentName',
        Header: 'Nationality',
        sort: true
    },
    {
        accessor: 'kyc',
        Header: 'KYC',
        class: 'd-flex align-items-center justify-content-center',
        sort: true,
        Cell: ({ row }) => {
            const kycValue = row.original.kyc;
            const dateNextCheck = row.original.dateNextCheck ? new Date(row.original.dateNextCheck) : null;

            let isKycValid = kycValue;

            if (dateNextCheck) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (dateNextCheck < today) {
                    isKycValid = false;
                }
            }

            return (
                <div className="text-center">
                    <FontAwesomeIcon
                        icon={isKycValid ? 'check' : 'times'}
                        className={isKycValid ? 'text-success' : 'text-danger'}
                    />
                </div>
            );
        }
    },
    {
        accessor: 'dateCheckedOn',
        Header: 'Documents checked on',
        sort: true,
        Cell: ({ value }) => <div className="text-center"> <span className="badge badge-soft-secondary text-primary">{value ? new Date(value).toLocaleDateString() : ''}</span></div>
    },
    {
        accessor: 'dateNextCheck',
        Header: 'Next documents check',
        sort: true,
        Cell: ({ value }) => {
            if (!value) return '';
            const date = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const badgeClass = date < today ? 'badge badge-soft-danger' :
                (date <= new Date(today.setMonth(today.getMonth() + 1))) ? 'badge badge-soft-warning' : 'badge badge-soft-success';

            return <div className="text-center"> {badgeClass ?
                <span className={badgeClass}>{date.toLocaleDateString()}</span> :
                date.toLocaleDateString()}</div>;
        }
    },
    {
        accessor: 'notes',
        Header: 'Notes',
        sort: true
    }
];

const ClientList = () => {

    const [nationalityOptions, setNationalityOptions] = useState([]);
    const [isLoadingNationalities, setIsLoadingNationalities] = useState(true);

    // Fetch nationalities for the dropdown
    useEffect(() => {
        document.title = "Retail clients";
        axiosInstance.get(`${window.location.origin}/parameters/countries`)
            .then((res) => {
                setNationalityOptions(res.data);
                setIsLoadingNationalities(false);
            })
            .catch((error) => {
                console.error("Error fetching nationalities:", error);
                setIsLoadingNationalities(false);
            });
    }, []);

    const [exportLoading, setExportLoading] = useState(false);

    const [state, setState] = useState({
        sortBy: [
            { id: 'name', desc: false }
        ],
        total: 0,
        totalPages: 0,
        remoteData: [],
        pageSize: 10,
        currentPage: 0
    });
    const [isLoading, showLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const fetchIdRef = React.useRef(0);

    const getClients = React.useCallback(({ page, size, sortBy }) => {
        const fetchId = ++fetchIdRef.current;
        if (fetchId === fetchIdRef.current) {
            showLoading(true);

            let formQuery = $("#tableFilters").serialize();

            const activeClient = $("#ActiveClient").val();
            if (activeClient) formQuery += `&ActiveClient=${activeClient}`;

            const kyc = $("#kyc").val();
            if (kyc) formQuery += `&KYC=${kyc}`;

            const risk = $("#Risk").val();
            if (risk) formQuery += `&Risk=${risk}`;

            const pep = $("#PEP").val();
            if (pep) formQuery += `&PEP=${pep}`;

            const physMet = $("#PhysMet").val();
            if (physMet) formQuery += `&PhysMet=${physMet}`;

            const specialTransac = $("#SpecialTransac").val();
            if (specialTransac) formQuery += `&SpecialTransac=${specialTransac}`;

            console.log("Complete form query:", formQuery);

            formQuery += `${(formQuery.length > 0 ? "&" : "")}page=${page}`;
            formQuery += `&take=${size}`;

            if (sortBy && sortBy.length > 0) {
                formQuery += `&sortColumn=${sortBy[0].id}&desc=${sortBy[0].desc}`;
            }

            axiosInstance.get(`${window.location.origin}/clients?${formQuery}`)
                .then((res) => {
                    setState({
                        sortBy: sortBy,
                        total: res.data.count,
                        totalPages: Math.ceil(res.data.count / (size * 1.0)),
                        remoteData: res.data.data,
                        pageSize: size,
                        currentPage: page
                    });
                    showLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching clients:", error);
                    toast.error("Failed to load clients data");
                    showLoading(false);
                });
        }
    }, []);

    const search = () => {
        getClients({ page: 0, size: state.pageSize, sortBy: state.sortBy });
    };

    const debouncedSearch = useCallback(
        debounce(() => {
            search();
        }, 500),
        []
    );

    useEffect(() => {
        const nameInput = document.getElementById("Name");
        const notesInput = document.getElementById("Notes");

        nameInput.addEventListener("input", debouncedSearch);
        notesInput.addEventListener("input", debouncedSearch);

        return () => {
            nameInput.removeEventListener("input", debouncedSearch);
            notesInput.removeEventListener("input", debouncedSearch);
        };
    }, [debouncedSearch]);

    const loadOptionsFromCache = (inputValue) => {
        return Promise.resolve(
            nationalityOptions.filter(option =>
                option.residentName.toLowerCase().includes(inputValue.toLowerCase())
                || option.countryName.toLowerCase().includes(inputValue.toLowerCase())
            )
        );
    };

    const CustomValueContainer = ({ children, ...props }) => {
        return (
            <ValueContainer {...props}>
                <Placeholder {...props} isFocused={props.isFocused}>
                    {props.selectProps.placeholder}
                </Placeholder>
                {React.Children.map(children, (child) =>
                    child && child.type !== Placeholder ? child : null
                )}
            </ValueContainer>
        );
    };

    const exportTableToExcel = () => {
        setExportLoading(true);
        let formQuery = $("#tableFilters").serialize();

        const activeClient = $("#ActiveClient").val();
        if (activeClient) formQuery += `&ActiveClient=${activeClient}`;

        const kyc = $("#kyc").val();
        if (kyc) formQuery += `&KYC=${kyc}`;

        const risk = $("#Risk").val();
        if (risk) formQuery += `&Risk=${risk}`;

        const pep = $("#PEP").val();
        if (pep) formQuery += `&PEP=${pep}`;

        const physMet = $("#PhysMet").val();
        if (physMet) formQuery += `&PhysMet=${physMet}`;

        const specialTransac = $("#SpecialTransac").val();
        if (specialTransac) formQuery += `&SpecialTransac=${specialTransac}`;

        console.log("Complete form query:", formQuery);

        formQuery += `${(formQuery.length > 0 ? "&" : "")}page=${state.currentPage}`;
        //formQuery += `&take=${state.pageSize}`;

        if (state.sortBy && state.sortBy.length > 0) {
            formQuery += `&sortColumn=${state.sortBy[0].id}&desc=${state.sortBy[0].desc}`;
        }
        
        axiosInstance.get(`${window.location.origin}/clients/export-data?fileName=Clients&${formQuery}`,
            { responseType: 'blob' })
            .then(response => {
                let blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.download = `clients${moment(new Date()).format("DDMMYYYY")}.xlsx`
                link.click()
                setExportLoading(false);
            }).catch(e => {
                toast.error("There was an error exporting the data", { theme: 'colored' })
                setExportLoading(false);
            })
    }

    /*Table layout*/
    const clientTable = () => {

        /*Filters*/
        const renderFilters = (visible) => {
            if (visible) {
                return (<></>);
            } else {
                return (
                    <>
                        <form id="tableFilters">
                            <Row className="mb-3 g-3">
                                <Col lg={3}>
                                    <Form.Group>
                                        <Form.Label>Active Client</Form.Label>
                                        <Form.Select
                                            id="ActiveClient"
                                            className="me-2"
                                            onChange={() => debouncedSearch()}
                                        >
                                            <option value="">Both</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col lg={3}>
                                    <TextFilter
                                        label="Name"
                                        id="Name"
                                        placeholder="search..."
                                        onChange={() => debouncedSearch()}
                                    />
                                </Col>

                                <Col lg={3}>
                                    <Form.Group>
                                        <Form.Label>Nationality</Form.Label>

                                        <AsyncSelect
                                            id="NationalitySelect"
                                            isClearable={true}
                                            getOptionLabel={option => option.residentName}
                                            getOptionValue={option => option.id}
                                            className="h-100 w-100 flex-fill w-50 fs--1rem"
                                            placeholder="Any nationality"
                                            loadOptions={loadOptionsFromCache}
                                            isLoading={isLoadingNationalities}
                                            defaultOptions={nationalityOptions}
                                            classNamePrefix="selectCustomFontSelect"
                                            onChange={(selectedOption) => {
                                                $("#nationalityId").val(selectedOption ? selectedOption.id : "");
                                                debouncedSearch();
                                            }}
                                        >
                                        </AsyncSelect>

                                        <input type="hidden" id="nationalityId" name="nationalityId" />
                                    </Form.Group>
                                </Col>

                                <Col lg={3}>
                                    <Form.Group>
                                        <Form.Label>KYC</Form.Label>
                                        <Form.Select
                                            id="kyc"
                                            className="me-2"
                                            onChange={() => debouncedSearch()}
                                        >
                                            <option value="">Both</option>
                                            <option value="true">Valid</option>
                                            <option value="false">Invalid</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3 g-3">
                                <Col lg={3}>
                                    <DateRangeFilter
                                        label="Documents checked on"
                                        id="dateCheckedOn"
                                        onChange={() => {
                                            debouncedSearch();
                                        }}
                                    />
                                </Col>
                                <Col lg={3}>
                                    <DateRangeFilter
                                        label="Next documents check"
                                        id="dateNextCheck"
                                        onChange={() => {
                                            debouncedSearch();
                                        }}

                                        validateBeforeChange={true}
                                    />
                                </Col>
                                <Col lg={3}>
                                    <TextFilter
                                        label="Notes"
                                        id="Notes"
                                        placeholder="search..."
                                        onChange={() => debouncedSearch()}
                                    />
                                </Col>
                                {/*<Col lg={3}>*/}
                                {/*    <Form.Group>*/}
                                {/*        <Form.Label>Risk Level</Form.Label>*/}
                                {/*        <Form.Select */}
                                {/*            id="Risk" */}
                                {/*            className="me-2"*/}
                                {/*            onChange={() => debouncedSearch()}*/}
                                {/*        >*/}
                                {/*            <option value="">All</option>*/}
                                {/*            <option value="Low">Low</option>*/}
                                {/*            <option value="Standard">Standard</option>*/}
                                {/*            <option value="High">High</option>*/}
                                {/*        </Form.Select>*/}
                                {/*    </Form.Group>*/}
                                {/*</Col>*/}
                            </Row>
                            <Row className="mb-3 g-3">
                                {/*<Col lg={3}>*/}
                                {/*    <Form.Group>*/}
                                {/*        <Form.Label>PEP</Form.Label>*/}
                                {/*        <Form.Select */}
                                {/*            id="PEP" */}
                                {/*            className="me-2"*/}
                                {/*            onChange={() => debouncedSearch()}*/}
                                {/*        >*/}
                                {/*            <option value="">Both</option>*/}
                                {/*            <option value="true">Yes</option>*/}
                                {/*            <option value="false">No</option>*/}
                                {/*        </Form.Select>*/}
                                {/*    </Form.Group>*/}
                                {/*</Col>*/}
                                {/*<Col lg={3}>*/}
                                {/*    <Form.Group>*/}
                                {/*        <Form.Label>Physically Met</Form.Label>*/}
                                {/*        <Form.Select */}
                                {/*            id="PhysMet" */}
                                {/*            className="me-2"*/}
                                {/*            onChange={() => debouncedSearch()}*/}
                                {/*        >*/}
                                {/*            <option value="">Both</option>*/}
                                {/*            <option value="true">Yes</option>*/}
                                {/*            <option value="false">No</option>*/}
                                {/*        </Form.Select>*/}
                                {/*    </Form.Group>*/}
                                {/*</Col>*/}
                                {/*<Col lg={3}>*/}
                                {/*    <Form.Group>*/}
                                {/*        <Form.Label>Special Transaction</Form.Label>*/}
                                {/*        <Form.Select */}
                                {/*            id="SpecialTransac" */}
                                {/*            className="me-2"*/}
                                {/*            onChange={() => debouncedSearch()}*/}
                                {/*        >*/}
                                {/*            <option value="">Both</option>*/}
                                {/*            <option value="true">Yes</option>*/}
                                {/*            <option value="false">No</option>*/}
                                {/*        </Form.Select>*/}
                                {/*    </Form.Group>*/}
                                {/*</Col>*/}

                            </Row>
                        </form>
                    </>);
            }
        }
        return (
            <>
                <AdvanceTableWrapper
                    columns={columns}
                    sortable
                    initialSortBy={state.sortBy}
                    pagination
                    perPage={state.pageSize}
                    data={state.remoteData}
                    pageCount={state.totalPages}
                    fetchData={getClients}
                    initialPage={state.currentPage}
                    isLoading={isLoading}
                >
                    {/*Filter row*/}
                    {renderFilters(showFilters)}

                    <AdvanceTable
                        trLinkById={`${window.location.origin}/components/clients/edit`}
                        table
                        headerClassName="bg-200 text-900 text-nowrap align-middle"
                        rowClassName="align-middle white-space-nowrap"
                        tableProps={{
                            bordered: true,
                            striped: true,
                            className: 'fs--1 mb-0 overflow-hidden'
                        }}
                    />
                    <div className="mt-3">
                        <AdvanceTableFooter
                            rowCount={state.total}
                            pageCount={state.totalPages}
                            table
                            rowInfo
                            navButtons
                            rowsPerPageSelection
                        />
                    </div>
                </AdvanceTableWrapper>
            </>
        );
    }

    return (
        <FalconComponentCard>
            <FalconComponentCard.Header
                light={false}
                className="border-bottom border-200"
            >
                {/*Action Buttons*/}
                <div className="d-flex flex-between-center">
                    <Col md="auto" lg={6}>
                        <h4>Retail clients</h4>
                    </Col>
                    <Col md="auto" lg={6} className="text-end">
                        <IconButton
                            variant="falcon-default"
                            size="sm"
                            icon="fa-filter"
                            transform="shrink-3"
                            className='me-2'
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <span className="d-none d-sm-inline-block ms-1">Filters</span>
                        </IconButton>
                        <Link to="/components/clients/create">
                            <IconButton
                                variant="primary"
                                size="sm"
                                icon="plus"
                                transform="shrink-3"
                                className='me-2'
                            >
                                <span className="d-none d-sm-inline-block ms-1">New</span>
                            </IconButton>
                        </Link>
                        <Button
                            variant="falcon-default"
                            size="sm"
                            onClick={exportTableToExcel}
                            disabled={exportLoading}
                        >
                            {exportLoading ? (
                                <Spinner animation="border" size="sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon="external-link-alt" className="me-2" transform="shrink-3" />
                                    Export
                                </>
                            )}
                        </Button>
                    </Col>
                </div>
            </FalconComponentCard.Header>
            <FalconComponentCard.Body
                children={clientTable()}
                scope={{
                    AdvanceTableWrapper,
                    AdvanceTable,
                    AdvanceTableFooter,
                    IconButton,
                    Link
                }}
                language="jsx"
                noInline
                noLight
            />
        </FalconComponentCard>
    );
};

export default ClientList;
